import { useMemo, useState } from 'react';
import { Plus, Receipt } from 'lucide-react';
import { useCreateCreditOrFee, useCreditsAndFees, useDeleteCreditOrFee } from '@/hooks/useCredits';
import { useClients, useNameLookup } from '@/hooks/useReference';
import { useBillingNouns } from '@/hooks/useSession';
import { formatDay, money, today } from '@/lib/format';
import type { Client, CreditFeeKind, CreditOrFee } from '@/types';
import { Screen } from '@m/ui/Screen';
import { ColorDot, Group, Row } from '@m/ui/List';
import { BarButton, Button, Empty, Segmented, SkeletonList } from '@m/ui/Controls';
import { Sheet, SheetBack } from '@m/ui/Sheet';
import { PickerList } from '@m/ui/Picker';
import { SwipeRow } from '@m/ui/SwipeRow';
import { withUndo } from '@m/ui/undo';
import { FieldRow, TextRow } from '../clients/forms';

/** Credits and fees waiting for the next invoice, and the ones already on one. */
export function CreditsFeesScreen() {
  const list = useCreditsAndFees({ pageSize: 100, sortField: 'date', sortOrder: 'desc' });
  const del = useDeleteCreditOrFee();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  const items = (list.data?.items ?? []).filter((i) => !hidden.has(i.id));
  const open = items.filter((i) => !i.applied && !i.invoiceId);
  const applied = items.filter((i) => i.applied || i.invoiceId);

  const row = (c: CreditOrFee, removable: boolean) => {
    const body = (
      <Row
        title={c.description || (c.kind === 'credit' ? 'Credit' : 'Fee')}
        subtitle={[c.clientName, c.date ? formatDay(c.date) : null, c.invoiceNumber ? `on ${c.invoiceNumber}` : null].filter(Boolean).join(' · ')}
        trailing={<Amount c={c} />}
      />
    );
    if (!removable) return <div key={c.id}>{body}</div>;
    return (
      <SwipeRow
        key={c.id}
        title={c.description}
        trailing={[
          {
            label: 'Delete',
            tone: 'danger',
            onSelect: () =>
              withUndo({
                message: `Deleted “${c.description}”`,
                hide: () => setHidden((s) => new Set(s).add(c.id)),
                restore: () =>
                  setHidden((s) => {
                    const n = new Set(s);
                    n.delete(c.id);
                    return n;
                  }),
                commit: () => del.mutate({ id: c.id })
              })
          }
        ]}
      >
        {body}
      </SwipeRow>
    );
  };

  return (
    <Screen
      title="Credits & fees"
      back="/more"
      backLabel="More"
      actions={
        <BarButton aria-label="Add a credit or fee" onClick={() => setAdding(true)}>
          <Plus className="size-[26px]" strokeWidth={2.2} />
        </BarButton>
      }
    >
      {list.isPending ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <Empty icon={<Receipt />} title="No credits or fees" body="A retainer, a discount or a filing fee waits here for the next invoice.">
          <Button onClick={() => setAdding(true)}>Add one</Button>
        </Empty>
      ) : (
        <>
          {open.length > 0 && (
            <Group header="Waiting for an invoice" footer="Added to the client's next invoice.">
              {open.map((c) => row(c, true))}
            </Group>
          )}
          {applied.length > 0 && <Group header="On an invoice">{applied.map((c) => row(c, false))}</Group>}
        </>
      )}
      <AddCreditFeeSheet key={String(adding)} open={adding} onClose={() => setAdding(false)} />
    </Screen>
  );
}

function Amount({ c }: { c: CreditOrFee }) {
  const credit = c.kind === 'credit';
  return (
    <span className={`tnum text-body font-medium ${credit ? 'text-emerald-700 dark:text-emerald-300' : ''}`}>
      {credit ? '−' : ''}
      {money(Math.abs(c.amount))}
      {c.isHourly ? '/h' : ''}
    </span>
  );
}

function AddCreditFeeSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nouns = useBillingNouns();
  const clients = useClients().data ?? [];
  const { clientName } = useNameLookup();
  const create = useCreateCreditOrFee(clientName);
  const [kind, setKind] = useState<CreditFeeKind>('fee');
  const [clientId, setClientId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [picking, setPicking] = useState(false);
  const active = useMemo(() => clients.filter((c) => c.status === 'active'), [clients]);

  const value = Number(amount);
  const valid = !!clientId && description.trim() !== '' && value > 0;
  const dirty = !!clientId || description !== '' || amount !== '';

  const save = () => {
    if (!valid) return;
    create.mutate({ clientId, kind, description: description.trim(), amount: value, date: today(), isHourly: false });
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      full
      dirty={dirty}
      title={picking ? nouns.client : `New ${kind}`}
      leading={picking ? <SheetBack label={`New ${kind}`} onClick={() => setPicking(false)} /> : <BarButton onClick={onClose}>Cancel</BarButton>}
      trailing={
        picking ? undefined : (
          <BarButton className="font-semibold" disabled={!valid} onClick={save}>
            Save
          </BarButton>
        )
      }
    >
      {picking ? (
        <PickerList<Client>
          items={active}
          value={clientId}
          noun={nouns.clientLower}
          nounPlural={nouns.clients.toLowerCase()}
          onSelect={(c) => {
            setClientId(c.id);
            setPicking(false);
          }}
          leading={(c) => <ColorDot id={c.id} />}
        />
      ) : (
        <div className="flex flex-col gap-6">
          <Segmented
            value={kind}
            onChange={setKind}
            options={[
              { value: 'fee', label: 'Fee' },
              { value: 'credit', label: 'Credit' }
            ]}
          />
          <Group>
            <Row
              title={nouns.client}
              detail={clients.find((c) => c.id === clientId)?.name ?? 'Choose'}
              onClick={() => setPicking(true)}
              chevron
            />
            <FieldRow label="Amount" inputMode="decimal" placeholder="$0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Group>
          <Group header="Description" footer={kind === 'credit' ? 'Taken off the next invoice.' : 'Added to the next invoice.'}>
            <TextRow placeholder={kind === 'credit' ? 'Retainer applied' : 'Court filing fee'} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Group>
        </div>
      )}
    </Sheet>
  );
}
